// Socket adapter (M8 Slice-1 · socket render) — turns the DEPLOYED socket emit into the
// flat facts the pure layout engine (_socket-layout.js) consumes.
//
// GROUND-TRUTHED against the live -06-27-01 boot (2026-06-28): sockets are authored on
// the fixture and emitted inline at `boot.fixtureCatalogue[kind].sockets[]` (today only
// `pile-table` — the Display Table pilot — carries them: FEATURED/FAN/PILE). Each socket:
//   { id, position{x,y,z} (LOCAL to the fixture), rotationY (local), scale, capacity,
//     acceptedProductTypes[], layout: "single"|"fan"|"pile"|…, extras{} }
// So the client COMPOSES world = anchorWorldTRS ∘ socketLocalTRS (the anchor = the fixture
// instance's world placement, resolved from the shell empty by _collectShopAnchors), and
// MAPS the authored layout vocabulary → the engine's primitive names.
//
// (DECISION-048 documented sockets RESOLVED onto scene.semantic.fixtures[*].sockets[]; the
// deployed reality is catalogue-local + client-compose. This adapter supports BOTH — prefers
// a scene-resolved socket when present, else composes from the catalogue — prefer-real.)
//
// Pure (no THREE/DOM) so it unit-tests headlessly. Yaw-only composition (fixtures rotate
// about Y), which is all the placements need.

// CANONICAL layout vocabulary = the engine's own primitive names (Coord 2026-06-29: no
// alias layer; Content emits these exact strings in socket extras.layout). Kept in sync
// with _socket-layout.js LAYOUT_ALGORITHMS (duplicated, not imported, so this module stays
// single-file pure-testable). socket.layout is consumed DIRECTLY once the re-mint lands.
const CANONICAL = new Set([
	"flat-pile", "fan-spread", "vertical-stack", "featured-hero",
	"spine-out-row", "face-out-shelf-row", "spinner-arc", "browse-bin", "explicit-slots",
]);

// ASK-107 A1 — the legacy alias map is DELETED, on its own stated condition. It carried its own
// deletion order ("DELETE this map once Content re-mints with canonical names"), and the condition
// is met: Coord counted the live boot and found 0 legacy values against 28 canonical ones
// (browse-bin ×8, fan-spread ×4, featured-hero ×4, face-out-shelf-row ×4, and the rest). Nothing on
// the platform emits an alias, so the branch only stood between an unknown value and its refusal.
// A non-canonical layout now resolves to null — the layout engine's "unknown → skip" path — which
// is the honest answer for a value nothing produces.
export function mapLayout(authored) {
	if (!authored) return null;
	return CANONICAL.has(authored) ? authored : null;
}

// World yaw (radians) from a quaternion {x,y,z,w}, Y-up.
function yawFromQuat(q) {
	if (!q || !Number.isFinite(q.w)) return null;
	const { x = 0, y = 0, z = 0, w } = q;
	return Math.atan2(2 * (w * y + x * z), 1 - 2 * (y * y + z * z));
}

/** Resolve an anchor/socket facing (yaw, radians) — explicit rotationY, else quaternion. */
export function resolveFacing(o) {
	if (Number.isFinite(o?.rotationY)) return o.rotationY;
	const q = yawFromQuat(o?.quaternion || o?.rotation);
	if (q !== null) return q;
	if (Number.isFinite(o?.rotation?.y)) return o.rotation.y;
	return 0;
}

/** Compose a socket's LOCAL TRS with the fixture instance's WORLD TRS (yaw-only) → world
 * position + world yaw. anchorWorld = { position{x,y,z}, rotationY }.
 *
 * Socket positions are authored in REAL METRES in the fixture GLB; the fixture instance is
 * placed by position + yaw only (fixtures aren't visually scaled). The anchor's `scale`
 * field is a LEGACY footprint hint (e.g. PILE_TABLE_ANCHOR scale [0.7,0.5,0.05]) — NOT a
 * child-transform scale — so it is deliberately ignored here. Verified against the live
 * shell 2026-06-28; flagged to Coord (drop the footprint-scale from socketed anchors). */
export function composeSocketWorld(anchorWorld, socketLocal) {
	const ap = anchorWorld?.position || { x: 0, y: 0, z: 0 };
	const ayaw = resolveFacing(anchorWorld);
	const lp = socketLocal?.position || { x: 0, y: 0, z: 0 };
	const c = Math.cos(ayaw), s = Math.sin(ayaw);
	return {
		position: { x: ap.x + (lp.x * c + lp.z * s), y: ap.y + lp.y, z: ap.z + (-lp.x * s + lp.z * c) },
		rotationY: ayaw + (Number.isFinite(socketLocal?.rotationY) ? socketLocal.rotationY : 0),
	};
}

// One catalogue socket + its composed world transform → engine facts.
function socketToFacts(socket, world, anchorName) {
	const accepts = socket?.acceptedProductTypes || socket?.accepts;
	return {
		socketId: socket?.id || socket?.socketId || "MAIN",
		anchorName: anchorName || null,
		position: world.position,
		rotationY: world.rotationY,
		layout: mapLayout(socket?.layout),
		capacity: socket?.capacity,
		accepts: Array.isArray(accepts) ? accepts : (accepts ? [accepts] : undefined),
		params: socket?.params,
		slots: socket?.slots,
		// ASK-154 seam 2 — the mint's derived browse verb (r55) rides through; absent stays absent (no key).
		...(socket?.browse != null ? { browse: socket.browse, browseSource: socket.browseSource ?? null } : {}),
	};
}

/** Products curated to ONE socket — GCG's resolve tags each product
 * `fixtureAssignments[] = { fixtureAnchor, socketId, slot }`, so filter by BOTH
 * fixtureAnchor AND socketId and order by slot. This renders the curated FILL per socket
 * (e.g. PILE 6, RACK 15) — not the socket capacity with empties, and no cross-socket
 * overlap. Falls back to anchor-only match when an assignment carries no socketId (legacy
 * feed). Pure. */
export function productsForSocket(allProducts, anchorName, socketId, typeFilter) {
	if (!anchorName) return [];
	const matched = [];
	// `typeFilter` is one type (legacy socket) or a Set of them (a grammar socket's whole
	// accepts-matched family — one string cannot say "everything under `product`").
	// An UNTYPED line passes (ASK-126 S3.3): absent is "no opinion", and a line whose assignment
	// names this anchor was seated by the resolver, whose decision is authoritative (D-061 §5) —
	// a decor line rides `products[]` with `renderAssetKey` and may carry no product type at all.
	// This filter guards against KNOWN cross-type mis-assignment, which an untyped line cannot be.
	const typeOk = (t) => t == null || (typeFilter instanceof Set ? typeFilter.has(t) : t === typeFilter);
	for (const p of (Array.isArray(allProducts) ? allProducts : [])) {
		if (typeFilter && !typeOk(p.type)) continue;
		const hit = (Array.isArray(p.fixtureAssignments) ? p.fixtureAssignments : [])
			.find((a) => a && a.fixtureAnchor === anchorName && (a.socketId == null || socketId == null || a.socketId === socketId));
		if (hit) matched.push({ p, slot: Number.isFinite(hit.slot) ? hit.slot : 0 });
	}
	matched.sort((a, b) => a.slot - b.slot);
	return matched.map((m) => m.p);
}

// DECISION-052 convergence — map a socket's authored layout → an existing COMPOSER
// slot-layout so the composer can place the socket's products. Grows as engine primitives
// are ported to composer slot-layouts. A kind whose socket maps here is composer-handled
// (registered for PASS 1 + skipped by the socket render path); unmapped → stays socket-path
// for now. browse-bin / spine-out-row are linear comic lanes = the proven linear-spine-row.
const SOCKET_LAYOUT_TO_SLOT_LAYOUT = Object.freeze({
	"browse-bin":    "browse-bin",     // volume-driven comic lane (longbox)
	"featured-hero": "featured-hero",  // engine-adapter composer slot-layouts (pile-table sockets)
	"fan-spread":    "fan-spread",
	"flat-pile":     "flat-pile",
	"explicit-slots": "explicit-slots",   // spinner: composer friction-spin interaction OWNS rendering
	//                                        (geometryOwned) — not a slot-layout, but converged.
	// spine-out-row → port if a book fixture is socketed.
});

/** The composer slot-layout for a socket's authored layout, or null if not yet ported. */
export function socketLayoutToComposerSlotLayout(layout) {
	return layout ? (SOCKET_LAYOUT_TO_SLOT_LAYOUT[layout] || null) : null;
}

// ASK-112 S4 — a socket that AUTHORS the D-062 grammar is composer-converged by that fact alone,
// with no `layout` name involved. Duplicated (not imported) from `packages/layout-grammar/src/grammar.js`
// `isGrammarSocket` for the same reason the CANONICAL set above is duplicated: this module stays
// single-file pure-testable. Keep the two in step — the canonical one is the engine's.
//
// This is what makes ASK-112 S2's `GRAMMAR_TO_LEGACY_LAYOUT` alias row DELETABLE. Without it the
// longbox — which has no registry pattern and is registered for PASS 1 purely by being "converged"
// — would stop being composed at all the moment its socket stopped carrying `layout: browse-bin`,
// and the boxes would simply not render. The alias was load-bearing in a place nothing named.
const GRAMMAR_DISTRIBUTIONS = new Set(["single", "run", "stack", "grid", "arc", "scatter"]);
const GRAMMAR_DOMAINS = new Set(["point", "spline", "surface", "volume"]);   // `volume`: ASK-139 F-139an (record-crate BIN_01)
function hasAuthoredGrammar(socket) {
	return socket?.grammarSource === "authored"
		&& GRAMMAR_DISTRIBUTIONS.has(socket?.distribution)
		&& GRAMMAR_DOMAINS.has(socket?.domain)
		&& !!socket?.pose;
}

/** Is this kind's catalogue socket converged to a composer slot-layout? Drives both
 * "register for PASS 1" and "skip in the socket render path" so they can't disagree. */
export function isComposerConvergedKind(kind, fixtureCatalogue) {
	const sockets = fixtureCatalogue?.[kind]?.sockets;
	if (Array.isArray(sockets) && sockets.some(hasAuthoredGrammar)) return true;
	return !!socketLayoutToComposerSlotLayout(sockets?.[0]?.layout);
}

/** Derive a fixture-catalogue kind from a shell anchor-empty name, registry-independent
 * (composer-retired kinds like longbox have no registry pattern). `LONGBOX_ANCHOR_01` →
 * `longbox` · `PILE_TABLE_ANCHOR` → `pile-table` · `SPINNER_RACK_ANCHOR_NN` → `spinner-rack`.
 * Returns null if the name isn't a `<KIND>_ANCHOR[_n]` empty. */
export function deriveFixtureKind(anchorName) {
	const m = /^(.+?)_ANCHOR(?:_\d+)?$/i.exec(anchorName || "");
	return m ? m[1].toLowerCase().replace(/_/g, "-") : null;
}

/** Does this anchor's fixture kind carry sockets? (drives prefer-socket-else-anchor per anchor) */
export function anchorHasSockets(anchor, fixtureCatalogue) {
	const kind = anchor?.fixtureKind;
	const list = kind && fixtureCatalogue?.[kind]?.sockets;
	return Array.isArray(list) && list.length > 0;
}

/** Engine-ready facts for every socket of one fixture instance (anchor). Composes each
 * catalogue socket's local TRS with the anchor's world TRS. [] if the kind has no sockets. */
export function socketsForAnchor(anchor, fixtureCatalogue) {
	const kind = anchor?.fixtureKind;
	const list = kind && fixtureCatalogue?.[kind]?.sockets;
	if (!Array.isArray(list)) return [];
	const anchorWorld = { position: anchor.position, rotationY: anchor.rotationY, scale: anchor.scale };
	return list.map((s) => socketToFacts(s, composeSocketWorld(anchorWorld, s), anchor.name));
}
